import { ChangeEvent, FC, FocusEvent, FormEvent, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSelector, shallowEqual } from 'react-redux';
import Constants from '../../constants/constants';
import Password, { PasswordValidation, Validity, PasswordErrorType } from '../services/Password';
import User from '../services/User';
import Button from '../components/Button';
import Input from '../components/Input';
import PasswordRequirements from '../components/PasswordRequirements';

type PasswordInput = {
  value: string;
  validation: PasswordValidation;
};

const Card = styled.div(({ theme }) => {
  const { color, shadow } = theme;

  return `
    background-color: ${color.grayscale[1000]};
    border-radius: 16px;
    box-shadow: ${shadow.dialog};
    padding: 38px 32px;
    width: max(65%, 928px);
    margin: 0 auto;
  `;
});

const Form = styled.form`
  display: grid;
  grid-template-columns: calc(60% - 2rem) 40%;
  grid-column-gap: 2rem;
`;

const Container = styled.section`
  width: 100%;
  padding: 0 max(6.42%, 224px);
`;

const Heading = styled.h1(({ theme }) => {
  const { color, type } = theme;

  return `
    font-size: ${type.size.huge};
    color: ${color.grayscale[0]};
    margin-bottom: 3rem;
    text-align: center;
  `;
});

const InputGroup = styled.div`
  margin-bottom: 2rem;
`;

type UserData = {
  firstName: string;
  lastName: string;
  login: string;
};

const PasswordMessage = styled.div(({ theme }) => {
  const { color, type } = theme;

  return `
    text-align: center;
    color: ${color.grayscale[0]};
    font-size: ${type.size.regular};
    margin-bottom: 1rem;
  `;
});

const ContactList = styled.ul(({ theme }) => {
  const { color, type } = theme;

  return `
    color: ${color.grayscale[0]};
    font-size: ${type.size.regular};
    margin: 1rem 0 0.5rem;
    padding: 0 0 0 1rem;

    li {
      margin-bottom: 0.5rem;
    }
  `;
});

const ErrorMessage = styled.div(({ theme }) => {
  const { color, type } = theme;

  return `
    color: ${color.primary.regular};
    font-weight: ${type.weight.bold};
    text-align: center;
    margin-bottom: 1rem;
  `;
});

export enum ResetReason {
  FORCED,
  USER_INITIATED,
}

enum PageStatus {
  LOADING,
  LOADED,
  ERROR,
  COMPLETE,
  REPEAT_PASSWORD,
}

const ResetPassword: FC = () => {
  // TODO: Set up typing for redux store
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const user = useSelector((state: any) => state.user, shallowEqual);
  const resetReason = user.requirePasswordChange ? ResetReason.FORCED : ResetReason.USER_INITIATED;
  const [userData, setUserData] = useState<UserData | undefined>(undefined);
  const [confirm, setConfirm] = useState<string>('');
  const [formInvalid, setFormInvalid] = useState<boolean>(false);
  const [pageStatus, setPageStatus] = useState<PageStatus>(PageStatus.LOADING);
  const initialPassword = {
    value: '',
    validation: {
      valid: Validity.UNCHECKED,
      requirements: {
        username: Validity.UNCHECKED,
        firstName: Validity.UNCHECKED,
        lastName: Validity.UNCHECKED,
        blockedWords: Validity.UNCHECKED,
        symbol: Validity.UNCHECKED,
        uppercase: Validity.UNCHECKED,
        lowercase: Validity.UNCHECKED,
        number: Validity.UNCHECKED,
        length: Validity.UNCHECKED,
      },
    },
  };
  const [password, setPassword] = useState<PasswordInput>(initialPassword);

  useEffect(() => {
    setFormInvalid(password.validation.valid !== Validity.VALID || confirm !== password.value);
  }, [password, confirm]);

  useEffect(() => {
    if (typeof user?.currentUserId === 'number') {
      User.getUsername(user.currentUserId).then((response) => {
        if (response.success) {
          setUserData(response.userData);
          setPageStatus(PageStatus.LOADED);
        } else {
          setPageStatus(PageStatus.ERROR);
        }
      });
    }
  }, [user.currentUserId]);

  const handlePassInput = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const { valid, requirements } = Password.validatePassword(
      value,
      userData?.login,
      userData?.firstName,
      userData?.lastName
    );

    setPassword({
      value,
      validation:
        password.validation.valid !== Validity.UNCHECKED
          ? { valid, requirements }
          : { ...password.validation, requirements },
    });
  };

  const handlePassBlur = (e: FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const validation = Password.validatePassword(value, userData?.login, userData?.firstName, userData?.lastName);

    setPassword({ value, validation });
  };

  const handleConfirmInput = (e: ChangeEvent<HTMLInputElement>) => {
    setConfirm(e.target.value);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    setPageStatus(PageStatus.LOADING);

    Password.submitPasswordChange(password.value).then((response) => {
      if (response.success) {
        setPageStatus(PageStatus.COMPLETE);
        const currURL = new URL(window.location.href);
        const purl = encodeURI(currURL.origin);
        window.location.href = `${purl}/password-success`;
      } else {
        if (response.error === PasswordErrorType.REPEAT_PASSWORD) {
          setPageStatus(PageStatus.REPEAT_PASSWORD);
          setConfirm('');
          setPassword(initialPassword);
        } else {
          setPageStatus(PageStatus.ERROR);
        }
      }
    });
  };

  const showForm = pageStatus !== PageStatus.COMPLETE && pageStatus !== PageStatus.ERROR;

  return (
    <Container>
      <Heading>Change Password</Heading>
      {resetReason === ResetReason.FORCED && (
        <PasswordMessage>
          You are required to change your password. Please use the form below to create a new password.
        </PasswordMessage>
      )}

      {
        // Temporary till we open up other password change routes
        pageStatus === PageStatus.ERROR && (
          <Card>
            <h3>Error</h3>

            <p>Please contact our customer support team if you would like to change your password:</p>

            <ContactList>
              <li>By phone: {Constants.CS_PHONE}</li>
              <li>
                By email: <a href={`mailto:${Constants.CS_EMAIL}`}>{Constants.CS_EMAIL}</a>
              </li>
            </ContactList>
          </Card>
        )
      }

      {showForm && (
        <Card>
          <Form onSubmit={handleSubmit}>
            <div>
              <InputGroup>
                <Input
                  type="password"
                  id="password"
                  name="password"
                  invalid={password.validation.valid === Validity.INVALID}
                  required
                  onChange={handlePassInput}
                  onBlur={handlePassBlur}
                  value={password.value}
                  label={`${ResetReason.FORCED ? 'New ' : ''}Password`}
                  errors="Password does not meet minimum requirements."
                  disabled={pageStatus === PageStatus.LOADING}
                />
              </InputGroup>

              <InputGroup>
                <Input
                  type="password"
                  id="confirm"
                  name="confirm"
                  invalid={confirm.length > 0 && confirm !== password.value}
                  required
                  onChange={handleConfirmInput}
                  value={confirm}
                  label="Confirm Password"
                  errors="Password and Confirm Password must match."
                  disabled={pageStatus === PageStatus.LOADING}
                />
              </InputGroup>
              {pageStatus === PageStatus.REPEAT_PASSWORD && (
                <ErrorMessage aria-live="assertive">Cannot use previous password</ErrorMessage>
              )}
              <Button
                type="submit"
                disabled={pageStatus === PageStatus.LOADING || formInvalid ? true : false}
                fullWidth
              >
                UPDATE PASSWORD
              </Button>
            </div>

            <PasswordRequirements requirements={password.validation.requirements} />
          </Form>
        </Card>
      )}
    </Container>
  );
};

export default ResetPassword;
